import moment, { Moment } from 'moment-timezone';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { CommunicationResponse } from './communication-response';
import { Organization } from './organization';
import { User } from './user';
import { UploadedFile } from './uploaded-file';


export const onDemandInvoice = 'On-Demand Invoice';
export const customPdf = 'Custom PDF';
export const billPayCheck = 'Bill Pay Check';
export const violationLetter = 'Violation Letter';

export class CommunicationActivity
{
    id: number;
    organizationId: number;
    senderId: number;
    recipientId: number;
    body: string;
    type: string;
    createdAt: Moment;
    updatedAt: Moment;

    category: string;
    recipient: User;
    sender: User;
    senderName: string;
    recipientName: string;
    recipientAddress: string;
    recipientEmail: string;
    recipientPhone: string;
    subject: string;
    organization: Organization;
    responses: CommunicationResponse[] = [];
    sentType: string;

    // misc bool to show hide lob timeline in UI
    isShowingTimeline ? = false;
    // misc bool to show hide cancellation message in UI
    isCancelling ? = false;
    // if this belongs to a lob item that has been cancelled
    cancelled ? = false;

    fileAttachments: UploadedFile[] = [];

    // Scheduled email fields
    scheduledSendAt: string | null = null;
    amenityShareBookingId: number | null = null;
    amenityId: number | null = null;
    recipientCount: number | null = null;
    isSummary: boolean = false;

    constructor(communicationActivity: any) {
        if (communicationActivity.id) {
            this.id = communicationActivity.id;
        }
        if (communicationActivity.organizationId) {
            this.organizationId = communicationActivity.organizationId;
        }
        if (communicationActivity.senderId) {
            this.senderId = communicationActivity.senderId;
        }
        if (communicationActivity.recipientId) {
            this.recipientId = communicationActivity.recipientId;
        }
        this.subject = communicationActivity.subject || '';
        if (communicationActivity.body) {
            this.body = communicationActivity.body;
        }
        if (communicationActivity.type) {
            this.type = communicationActivity.type;
            switch (this.type) {
                case 'email':
                    this.sentType = 'Email';
                    break;
                case 'letter':
                    this.sentType = customPdf;
                    break;
                case 'paper':
                    this.sentType = onDemandInvoice;
                    break;
                case 'violation_letter':
                    this.sentType = violationLetter;
                    break;
            }
        }
        if (communicationActivity.senderName) {
            this.senderName = communicationActivity.senderName;
        }
        if (communicationActivity.recipientName) {
            this.recipientName = communicationActivity.recipientName;
        }

        this.recipientAddress = communicationActivity.recipientAddress
                ? communicationActivity.recipientAddress.replace(', ', '<br />')
                : '';
        this.recipientPhone = communicationActivity.recipientPhone || '';
        this.recipientEmail = communicationActivity.recipientEmail || '';

        if (communicationActivity.category) {
            this.category = communicationActivity.category;
        }

        const timezone = LegFiJwtService.getTimezone();
        if (communicationActivity.createdAt) {
            this.createdAt = moment.utc(communicationActivity.createdAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (communicationActivity.updatedAt) {
            this.updatedAt = moment.utc(communicationActivity.updatedAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
        if (communicationActivity.deletedAt) {
            this.cancelled = true;
        }

        if (communicationActivity.recipient) {
            if (communicationActivity.recipientType.indexOf('User') > -1) {
                this.recipient = new User(communicationActivity.recipient);
            }
        }
        if (communicationActivity.sender) {
            this.sender = new User(communicationActivity.sender);
        }
        if (communicationActivity.organization) {
            this.organization = new Organization(communicationActivity.organization);
        }
        if (communicationActivity.responses) {
            this.responses = communicationActivity.responses.map((response) => {
                return new CommunicationResponse(response);
            });
            this.responses.sort((a: CommunicationResponse, b: CommunicationResponse) => {
                return a.id < b.id ? 1 : -1;
            });
        }

        if (communicationActivity.fileAttachments?.length > 0) {
            this.fileAttachments = communicationActivity.fileAttachments;
        }

        // Scheduled email fields
        this.scheduledSendAt = communicationActivity.scheduledSendAt || null;
        this.amenityShareBookingId = communicationActivity.amenityShareBookingId || null;
        this.amenityId = communicationActivity.amenityId || null;
        this.recipientCount = communicationActivity.recipientCount || null;
        this.isSummary = communicationActivity.isSummary || false;
    }

    /**
     * Check if this is a scheduled email (has 'scheduled' event and not yet sent)
     */
    isScheduled(): boolean {
        return this.responses.some(r => r.event === 'scheduled') &&
               !this.responses.some(r => ['sent', 'delivered', 'open', 'click', 'cancelled'].includes(r.event));
    }

    /**
     * Check if this scheduled email can be cancelled
     */
    canCancel(): boolean {
        return this.isScheduled();
    }

    /**
     * Check if this scheduled email can be edited
     */
    canEdit(): boolean {
        return this.isScheduled() && this.amenityShareBookingId !== null;
    }
}
